<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { computedAsync } from '@vueuse/core';
import axiosInstance from '../plugins/axios';
import { useUserVariablesStore } from '@/store/userVariables';
import { useRoute, useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useStyleStore } from '../store/styleStore';


const styleStore = useStyleStore();
const route = useRoute();
const router = useRouter();
const allFaqs = ref([]);
const userVariablesStore = useUserVariablesStore();
const defaultLanguage = ref('en');
const currentLanguage = ref<string>(route.query.lang as string || defaultLanguage.value);
const categoryList = ref<string[]>([]);
const activeTab = ref(0);
const selectedCategory = ref('');
const selectedQuestionIndex = ref<number | null>(null);
const faqFallbackTranslation = ref({});
const onFaqPage = computed(() => {
    return route.name === 'Faq' || route.name === 'faqCategory' || route.name === 'faqQuestion';
});

const translation = computedAsync(
    async () => {
        return await userVariablesStore.currentLanguageData;
    },
    null
);

// HTML Sanitize Funktion für Schema.org
const sanitizeHtml = (html: string): string => {
    if (!html) return '';

    // Erstelle ein temporäres DOM-Element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Extrahiere nur den Text-Inhalt (entfernt alle HTML-Tags)
    let textContent = tempDiv.textContent || tempDiv.innerText || '';

    // Entferne überschüssige Whitespaces und Zeilenumbrüche
    textContent = textContent.replace(/\s+/g, ' ').trim();

    // Escape spezielle JSON-Zeichen
    textContent = textContent
        .replace(/\\/g, '\\\\')  // Backslashes
        .replace(/"/g, '\\"')    // Anführungszeichen
        .replace(/\n/g, '\\n')   // Zeilenumbrüche
        .replace(/\r/g, '\\r')   // Carriage returns
        .replace(/\t/g, '\\t');  // Tabs

    return textContent;
};

const getFaqs = async () => {
    try {
        const res = await axiosInstance.get('/faq');
        allFaqs.value = res.data.faqs;
        await updateCategoryList();
        setSelectedCategoryFromUrl(); // Set category from URL after loading
        activeTab.value = 0;
    } catch (err) {
        console.log(err);
    }
};

const updateCategoryList = async () => {
    const categories = new Set<string>();

    allFaqs.value.forEach(faq => {
        const hasTranslation =
            faqFallbackTranslation.value?.faqs?.[faq._id] ||
            translation.value?.faqs?.[faq._id];

        if (faq.category && hasTranslation) {
            categories.add(faq.category);
        }
    });

    categoryList.value = Array.from(categories);
};


const mergeFaq = (faqId: string) => {
    const fallback = faqFallbackTranslation.value?.faqs?.[faqId];
    const override = translation.value?.faqs?.[faqId];

    if (!fallback && !override) return null;

    console.log('Fallback:', fallback);
    console.log('Override:', override);
    return {
        ...fallback,
        ...override
    };
};

const filteredFaqsByCategory = computed(() => {
    const categorizedFaqs: Record<string, any[]> = {};

    allFaqs.value.forEach(faq => {
        const merged = mergeFaq(faq._id);
        if (faq.category && merged) {
            if (!categorizedFaqs[faq.category]) {
                categorizedFaqs[faq.category] = [];
            }
            categorizedFaqs[faq.category].push(merged);
        }
    });

    return categorizedFaqs;
});

const filteredFaqs = computed(() => {
    if (!selectedCategory.value || !filteredFaqsByCategory.value[selectedCategory.value]) {
        return [];
    }
    return filteredFaqsByCategory.value[selectedCategory.value];
});

// URL-Parameter Handling
const updateUrlParams = (categoryId?: string, questionIndex?: number) => {
    const query = { ...route.query };

    if (categoryId) {
        query.category = categoryId;
    }

    if (questionIndex !== undefined && questionIndex !== null) {
        query.question = questionIndex.toString();
    }
    // Wichtig: question Parameter NICHT löschen wenn nicht explizit gesetzt

    router.replace({ ...route, query });
};

const setSelectedCategoryFromUrl = () => {
    const urlCategory = route.query.category as string;
    const urlQuestion = route.query.question as string;

    if (urlCategory && categoryList.value.includes(urlCategory)) {
        selectedCategory.value = urlCategory;
    } else if (categoryList.value.length > 0 && !urlCategory) {
        // Nur Default-Kategorie setzen wenn kein URL-Parameter vorhanden ist
        selectedCategory.value = categoryList.value[0];
    }

    if (urlQuestion && !isNaN(Number(urlQuestion))) {
        selectedQuestionIndex.value = Number(urlQuestion);
    } else {
        selectedQuestionIndex.value = null;
    }
};

// Computed für aktive Accordion-Werte
const activeAccordionValue = computed(() => {
    if (selectedQuestionIndex.value !== null && filteredFaqs.value[selectedQuestionIndex.value]) {
        return filteredFaqs.value[selectedQuestionIndex.value].question;
    }
    return "0"; // Default first item
});

// Handler für Accordion-Änderungen
const onAccordionChange = (value: string) => {
    const questionIndex = filteredFaqs.value.findIndex(faq => faq.question === value);
    if (questionIndex !== -1) {
        selectedQuestionIndex.value = questionIndex;
        // Beide Parameter explizit setzen
        const query = { ...route.query };
        query.category = selectedCategory.value;
        query.question = questionIndex.toString();
        router.replace({ ...route, query });
    }
};

// Handler für manuelle Kategorie-Änderung
const onCategoryChange = (newCategory: string) => {
    const query = { ...route.query };
    query.category = newCategory;
    // Frage-Parameter beibehalten oder entfernen je nach Kontext
    if (selectedQuestionIndex.value !== null) {
        delete query.question; // Frage zurücksetzen bei Kategorie-Wechsel
    }
    router.replace({ ...route, query });
    selectedQuestionIndex.value = null;
};

watch(
    () => route.query.lang,
    async (newLang: any) => {
        if (newLang) {
            currentLanguage.value = newLang;
            await getFaqs();
        } else {
            await router.replace({ ...route, query: { ...route.query, lang: defaultLanguage.value } });
        }
    },
    { immediate: true }
);

// Watch für URL-Parameter Änderungen
watch(
    () => [route.query.category, route.query.question],
    () => {
        setSelectedCategoryFromUrl();
    },
    { immediate: true }
);

// Watch für Kategorie-Änderungen
// Entfernt - wird jetzt über onCategoryChange manuell gehandhabt

watch(
    translation,
    async (newTranslation) => {
        if (newTranslation) {
            await initPage();
        }
    },
    { immediate: true }
);

watch(filteredFaqs, (newVal) => {
    // Entferne alle vorherigen FAQ-Schema-Scripts
    const existingScripts = document.querySelectorAll('[id^="faq-schema-"]');
    existingScripts.forEach(script => script.remove());

    // FAQ Schema nur für Übersichtsseiten hinzufügen
    if (newVal.length > 0) {
        const scriptId = `faq-schema-${selectedCategory.value}-${newVal.length}`;
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = scriptId;
        script.innerHTML = generateFAQSchema();
        document.head.appendChild(script);

        // Canonical URL hinzufügen
        addCanonicalUrl();
    } else {
        // Für spezifische Fragen: Canonical URL ohne Parameter
        addCanonicalUrl();
    }
});

// Funktion zum Ermitteln des Organisationsnamens basierend auf der Domain
const getOrganizationName = (): string => {
    const hostname = window.location.hostname;
    
    const domainMappings: Record<string, string> = {
        'www.tuev-nord.risk-navigator.ai': 'TÜV NORD',
        'www.tuv-sud.risk-navigator.ai': 'TÜV SÜD',
        'www.tuev-hessen.risk-navigator.ai': 'TÜV Hessen',
        'www.tuv-rheinland.risk-navigator.ai': 'TÜV Rheinland',
        'www.tuev-risk-navigator.ai': 'TÜV AI.Lab'
    };
    
    return domainMappings[hostname] || 'TÜV AI.Lab'; // Fallback
};

const generateFAQSchema = () => {
    if (!Array.isArray(filteredFaqs.value) || filteredFaqs.value.length === 0) {
        return '{}';
    }

    // Optimiertes FAQ Schema nach schema.org Spezifikation
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": filteredFaqs.value.map((faq, index) => ({
            "@type": "Question",
            "name": sanitizeHtml(faq.question),
            "text": sanitizeHtml(faq.question),
            "position": index + 1,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": sanitizeHtml(faq.answer),
                "inLanguage": currentLanguage.value,
                "author": {
                    "@type": "Organization",
                    "name": getOrganizationName()
                }
            }
        })),
        "inLanguage": currentLanguage.value,
        "about": {
            "@type": "Thing",
            "name": getCategoryName(selectedCategory.value)
        }
    };

    return JSON.stringify(faqSchema, null, 2);
};

const addCanonicalUrl = () => {
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
        existingCanonical.remove();
    }

    const baseUrl = window.location.origin + window.location.pathname;
    const canonicalUrl = currentLanguage.value && currentLanguage.value !== defaultLanguage.value
        ? `${baseUrl}?lang=${currentLanguage.value}`
        : baseUrl;

    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = canonicalUrl;
    document.head.appendChild(canonical);
};

const getCategoryName = (categoryKey: string): string => {
    const fallback = faqFallbackTranslation.value?.faqCategories?.[categoryKey]?.name;
    const override = translation.value?.faqCategories?.[categoryKey]?.name;
    return override || fallback || categoryKey;
};

const getFallBackTranslation = async () => {
    try {
        const res = await axiosInstance.get('/config/faqFallbackTranslation');

        const hasFallbacks =
            res.data &&
            Object.keys(res.data?.faqs || {}).length > 0 &&
            Object.keys(res.data?.faqCategories || {}).length > 0;

        faqFallbackTranslation.value = hasFallbacks ? res.data : null;
    } catch (err) {
        console.log(err);
        faqFallbackTranslation.value = null;
    }
};

const initPage = async () => {
    await getFallBackTranslation();

    if (translation.value) {
        await getFaqs();
    }

};

onMounted(async () => {
    await initPage();
    addCanonicalUrl();
});
</script>


<template>
    <div class="faq-wrapper">
        <div class="faq-container">
            <div v-if="categoryList.length > 0">
            <div class="flex justify-content-between faqFont" v-if="onFaqPage">
                <div class="flex font-bold faqTitle">
                    <h1>{{ typeof route.name === 'string' ? route.name.toUpperCase() : '' }}</h1>
                </div>
                <div class="flex" >
                    <Button class="p-button-text text-decoration-none faqStartClassification flex items-center justify-center" @click="router.push({ path: '/', query: { lang: currentLanguage } })">
                        <template #default>
                            <span class="flex align-items-center gap-2">
                                {{ translation?.staticTexts?.startClassification }}
                                <FontAwesomeIcon icon="arrow-right" />
                            </span>
                        </template>
                    </Button>
                </div>

            </div>
            <div class="row">
                <Select :defaultValue="categoryList[0]" v-model="selectedCategory" class="category-dropdown faqFont"
                    :options="categoryList" placeholder="Select a category">
                    <template #option="{ option }">
                        <div class="faqFont">{{ getCategoryName(option) }}</div>
                    </template>
                    <template #value="{ value }">
                        <div class="faqFont">{{ getCategoryName(value) }}</div>
                    </template>
                </Select>

            </div>

                <div clas="m-3">
                    <Accordion :value="activeAccordionValue" @update:value="(value) => onAccordionChange(value)">
                        <AccordionPanel v-for="(faq, index) in filteredFaqs" :key="index" :value="faq.question">
                            <AccordionHeader>
                                <span class="flex items-center gap-2 w-full">
                                    <span class="whitespace-nowrap faqQuestion" v-html="faq.question"></span>
                                </span>
                            </AccordionHeader>

                            <AccordionContent>
                                <div class="m-0 faqAnswer" v-html="faq.answer"></div>
                            </AccordionContent>
                        </AccordionPanel>
                    </Accordion>
                </div>
            </div>
            <div v-else>
                <div class="p-3 m-auto">
                    <p>
                        <Skeleton height="2rem" class="mb-2"></Skeleton>
                    </p>
                    <p>
                        <Skeleton height="2rem" class="mb-2"></Skeleton>
                    </p>
                    <p>
                        <Skeleton height="2rem" class="mb-2"></Skeleton>
                    </p>
                </div>
            </div>
        </div>

        <div class="faqDisclaimer faqFont text-muted">
            <p v-if="currentLanguage === 'de'" v-html="styleStore?.styles?.faqDisclaimerDe.value"></p>
            <p v-if="currentLanguage === 'en'" v-html="styleStore?.styles?.faqDisclaimerEn.value"></p>
        </div>
    </div>
</template>



<style scoped>
.category-dropdown {
    width: 97%;
    margin-bottom: 20px;
    margin-top: 20px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 0;
}

.p-accordionheader {
    padding-left: 0px !important;
    padding-right: 21px !important;
    padding-bottom: 5px !important;
}

.p-select-list-container {
    border-radius: 0;
}



.faq-container {
    min-height: 30vh;
    padding: 10px;
    padding-left: 20px;
    padding-right: 20px;
    z-index: 50000;
    display: flex;
    flex-direction: column;
}

.p-tabs {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
}

.faq-tablist {
    margin-top: auto;
    padding: 0px;
    background-color: white;
}

.p-tab-active {
    border: unset
}

.faq-item {
    margin-bottom: 15px;
}

.p-tab {

    padding: 7px;
    border: unset !important;
    color: white;
}

.p-focus {
    color: #000000 !important;
}

h4 {
    margin: 0;
    font-size: 1.1em;
}
</style>